// Celebration effects for game over screens
// Pixel fireworks + victory fanfare

// ============================================================================
// Pixel Fireworks
// ============================================================================

const COLORS = [
    '#f0c040', // gold
    '#4ecdc4', // teal
    '#ff6b6b', // coral
    '#c084fc', // purple
    '#34d399', // green
    '#fb923c', // orange
    '#f472b6', // pink
    '#60a5fa', // blue
    '#fbbf24', // amber
    '#a78bfa', // violet
];

const PIXEL_SIZE = 5;
const GRAVITY = 0.05;
const FIREWORK_COUNT = 8;
const PARTICLES_PER_BURST = 60;

// Interpolate hex color toward another
function lerpColor(a, b, t) {
    const ar = parseInt(a.slice(1, 3), 16), ag = parseInt(a.slice(3, 5), 16), ab = parseInt(a.slice(5, 7), 16);
    const br = parseInt(b.slice(1, 3), 16), bg = parseInt(b.slice(3, 5), 16), bb = parseInt(b.slice(5, 7), 16);
    const r = Math.round(ar + (br - ar) * t), g = Math.round(ag + (bg - ag) * t), bl = Math.round(ab + (bb - ab) * t);
    return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${bl.toString(16).padStart(2,'0')}`;
}

class Particle {
    constructor(x, y, color, speed) {
        const angle = Math.random() * Math.PI * 2;
        const spd = speed || (2 + Math.random() * 3.5);
        this.x = x;
        this.y = y;
        this.vx = Math.cos(angle) * spd;
        this.vy = Math.sin(angle) * spd;
        this.color = color;
        this.life = 1.0;
        this.decay = 0.006 + Math.random() * 0.01;
        this.size = PIXEL_SIZE * (0.5 + Math.random() * 0.5);
        // Some particles sparkle
        this.sparkle = Math.random() > 0.7;
        // Some particles leave comet tails
        this.hasTrail = Math.random() > 0.7;
        // Some particles fade toward gold as they die
        this.fadeColor = Math.random() > 0.6 ? '#fbbf24' : null;
        // Track previous positions for comet trail
        this.prevX = x;
        this.prevY = y;
        // Crackle: some particles pop tiny sparks when dying
        this.crackle = Math.random() > 0.85;
    }

    update() {
        this.prevX = this.x;
        this.prevY = this.y;
        this.x += this.vx;
        this.y += this.vy;
        this.vy += (this.gravity || GRAVITY);
        this.vx *= 0.985;
        this.life -= this.decay;
    }

    draw(ctx) {
        if (this.life <= 0) return;
        const alpha = Math.max(0, this.life);
        // Sparkle effect: flicker alpha
        const flickerAlpha = this.sparkle
            ? alpha * (0.5 + 0.5 * Math.sin(Date.now() * 0.02 + this.x))
            : alpha;

        // Color fade toward gold/white as particle dies
        const drawColor = this.fadeColor
            ? lerpColor(this.color, this.fadeColor, 1 - this.life)
            : this.color;

        // Comet trail — fading line from previous to current position
        if (this.hasTrail && this.life > 0.3) {
            ctx.globalAlpha = flickerAlpha * 0.4;
            ctx.strokeStyle = drawColor;
            ctx.lineWidth = Math.max(1, this.size * 0.4);
            ctx.beginPath();
            ctx.moveTo(Math.round(this.prevX), Math.round(this.prevY));
            ctx.lineTo(Math.round(this.x), Math.round(this.y));
            ctx.stroke();
        }

        ctx.globalAlpha = flickerAlpha;
        ctx.fillStyle = drawColor;
        const s = this.size * (0.3 + this.life * 0.7);
        ctx.fillRect(Math.round(this.x), Math.round(this.y), s, s);

        // Glow effect for brighter particles
        if (this.life > 0.6) {
            ctx.globalAlpha = flickerAlpha * 0.3;
            ctx.fillRect(Math.round(this.x) - 1, Math.round(this.y) - 1, s + 2, s + 2);
        }
    }
}

// Trail particle left behind by rockets
class TrailParticle {
    constructor(x, y, color) {
        this.x = x + (Math.random() - 0.5) * 3;
        this.y = y;
        this.color = color;
        this.life = 0.6 + Math.random() * 0.4;
        this.decay = 0.03 + Math.random() * 0.02;
        this.size = 2 + Math.random() * 2;
    }

    update() {
        this.y += 0.3;
        this.life -= this.decay;
    }

    draw(ctx) {
        if (this.life <= 0) return;
        ctx.globalAlpha = this.life * 0.6;
        ctx.fillStyle = this.color;
        ctx.fillRect(Math.round(this.x), Math.round(this.y), this.size, this.size);
    }
}

const BURST_PATTERNS = ['scatter', 'willow', 'palm', 'crossette'];

class Firework {
    constructor(canvas) {
        this.canvas = canvas;
        this.x = canvas.width * (0.1 + Math.random() * 0.8);
        this.y = canvas.height + 10;
        this.targetY = canvas.height * (0.1 + Math.random() * 0.35);
        this.vy = -(5 + Math.random() * 3);
        this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
        this.exploded = false;
        this.particles = [];
        this.trailParticles = [];
        this.trailTimer = 0;
        this.flash = 0; // explosion flash countdown
        // Some fireworks are "big" with more particles
        this.isBig = Math.random() > 0.6;
        this.pattern = BURST_PATTERNS[Math.floor(Math.random() * BURST_PATTERNS.length)];
        // Some rockets spiral on the way up
        this.spiral = Math.random() > 0.6;
        this.spiralPhase = Math.random() * Math.PI * 2;
        this.spiralSpeed = 0.15 + Math.random() * 0.1;
        this.spiralRadius = 1.5 + Math.random() * 1.5;
        this.baseX = this.x; // center of spiral
    }

    update() {
        if (!this.exploded) {
            this.y += this.vy;
            this.trailTimer++;
            // Spiral rockets wobble side-to-side
            if (this.spiral) {
                this.spiralPhase += this.spiralSpeed;
                this.x = this.baseX + Math.sin(this.spiralPhase) * this.spiralRadius;
            }
            // Emit trail particles
            if (this.trailTimer % 2 === 0) {
                this.trailParticles.push(new TrailParticle(this.x, this.y, this.color));
            }
            if (this.y <= this.targetY) {
                this.explode();
            }
        }

        for (let i = this.particles.length - 1; i >= 0; i--) {
            this.particles[i].update();
            if (this.particles[i].life <= 0) {
                const dying = this.particles[i];
                // Crossette: spawn secondary burst when a split particle dies
                if (dying.splits) {
                    for (let j = 0; j < 12; j++) {
                        const sp = new Particle(dying.x, dying.y, dying.splitColor, 1 + Math.random() * 1.5);
                        sp.decay = 0.015 + Math.random() * 0.01;
                        sp.size = PIXEL_SIZE * 0.6;
                        this.particles.push(sp);
                    }
                }
                // Crackle: tiny gold sparks pop off dying particles
                if (dying.crackle) {
                    for (let j = 0; j < 3 + Math.floor(Math.random() * 3); j++) {
                        const sp = new Particle(dying.x, dying.y, '#fbbf24', 0.5 + Math.random() * 1);
                        sp.decay = 0.03 + Math.random() * 0.02;
                        sp.size = PIXEL_SIZE * 0.3;
                        sp.hasTrail = false;
                        sp.crackle = false;
                        this.particles.push(sp);
                    }
                }
                this.particles.splice(i, 1);
            }
        }

        for (let i = this.trailParticles.length - 1; i >= 0; i--) {
            this.trailParticles[i].update();
            if (this.trailParticles[i].life <= 0) {
                this.trailParticles.splice(i, 1);
            }
        }
    }

    explode() {
        this.exploded = true;
        this.flash = 6; // bright flash frames

        // Pick 2-3 colors for this burst
        const burstColors = [this.color];
        burstColors.push(COLORS[Math.floor(Math.random() * COLORS.length)]);
        if (Math.random() > 0.5) {
            burstColors.push(COLORS[Math.floor(Math.random() * COLORS.length)]);
        }
        const pickColor = () => burstColors[Math.floor(Math.random() * burstColors.length)];
        const count = this.isBig ? PARTICLES_PER_BURST * 1.5 : PARTICLES_PER_BURST;

        switch (this.pattern) {
            case 'willow': {
                // Heavy drooping particles — weeping willow effect
                for (let i = 0; i < count; i++) {
                    const p = new Particle(this.x, this.y, pickColor(), 1.5 + Math.random() * 2.5);
                    p.gravity = 0.12;
                    p.decay = 0.004 + Math.random() * 0.005;
                    p.size = PIXEL_SIZE * (0.4 + Math.random() * 0.3);
                    this.particles.push(p);
                }
                break;
            }
case 'palm': {
                // Particles mostly shoot upward then arc down
                for (let i = 0; i < count; i++) {
                    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.2;
                    const speed = 3 + Math.random() * 3;
                    const p = new Particle(this.x, this.y, pickColor());
                    p.vx = Math.cos(angle) * speed;
                    p.vy = Math.sin(angle) * speed;
                    p.gravity = 0.08;
                    p.decay = 0.004 + Math.random() * 0.006;
                    this.particles.push(p);
                }
                break;
            }
            case 'crossette': {
                // Fewer fast particles that spawn secondary bursts
                const armCount = 6 + Math.floor(Math.random() * 6);
                for (let i = 0; i < armCount; i++) {
                    const angle = (i / armCount) * Math.PI * 2;
                    const speed = 4 + Math.random() * 2;
                    const p = new Particle(this.x, this.y, pickColor());
                    p.vx = Math.cos(angle) * speed;
                    p.vy = Math.sin(angle) * speed;
                    p.decay = 0.02; // die fast to trigger split
                    p.size = PIXEL_SIZE * 1.2;
                    p.splits = true; // marker for secondary burst
                    p.splitColor = pickColor();
                    this.particles.push(p);
                }
                break;
            }
            default: {
                // Classic scatter
                for (let i = 0; i < count; i++) {
                    this.particles.push(new Particle(this.x, this.y, pickColor()));
                }
                // Add a ring for big scatter fireworks
                if (this.isBig) {
                    for (let i = 0; i < 24; i++) {
                        const angle = (i / 24) * Math.PI * 2;
                        const p = new Particle(this.x, this.y, burstColors[0]);
                        p.vx = Math.cos(angle) * 4.5;
                        p.vy = Math.sin(angle) * 4.5;
                        p.decay = 0.008;
                        p.size = PIXEL_SIZE;
                        this.particles.push(p);
                    }
                }
            }
        }
    }

    draw(ctx) {
        // Draw trail particles
        for (const tp of this.trailParticles) {
            tp.draw(ctx);
        }

        // Draw rocket
        if (!this.exploded) {
            ctx.globalAlpha = 1;
            ctx.fillStyle = this.color;
            ctx.fillRect(Math.round(this.x) - 1, Math.round(this.y), 3, 4);
            // Bright tip
            ctx.fillStyle = '#fff';
            ctx.fillRect(Math.round(this.x), Math.round(this.y) - 1, 1, 2);
        }

        // Explosion flash — brief white glow at burst point
        if (this.flash > 0) {
            const radius = (7 - this.flash) * 8;
            ctx.globalAlpha = this.flash / 8;
            ctx.fillStyle = '#fff';
            ctx.beginPath();
            ctx.arc(this.x, this.y, radius, 0, Math.PI * 2);
            ctx.fill();
            this.flash--;
        }

        // Draw particles
        for (const p of this.particles) {
            p.draw(ctx);
        }
        ctx.globalAlpha = 1;
    }

    get done() {
        return this.exploded && this.particles.length === 0 && this.trailParticles.length === 0;
    }
}

let animationId = null;
let canvasEl = null;

/**
 * Start pixel fireworks animation
 * @param {string} [forcePattern] - Force a specific burst pattern for all fireworks
 * @returns {function} cleanup function to stop the animation
 */
export function startFireworks(forcePattern) {
    // Create canvas overlay
    canvasEl = document.createElement('canvas');
    canvasEl.className = 'fireworks-canvas';
    canvasEl.style.cssText = `
        position: fixed;
        top: 0; left: 0;
        width: 100%; height: 100%;
        pointer-events: none;
        z-index: 1001;
    `;
    document.body.appendChild(canvasEl);

    const ctx = canvasEl.getContext('2d');
    const resize = () => {
        canvasEl.width = window.innerWidth;
        canvasEl.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const fireworks = [];
    let spawnTimer = 0;
    let totalSpawned = 0;
    const maxFireworks = FIREWORK_COUNT * 5; // 5 waves

    function frame() {
        // Slight fade instead of full clear for trail effect
        ctx.fillStyle = 'rgba(0, 0, 0, 0.15)';
        ctx.fillRect(0, 0, canvasEl.width, canvasEl.height);

        // Spawn fireworks — rapid at first, then taper off
        spawnTimer++;
        if (totalSpawned < maxFireworks) {
            // First wave: rapid (every 4 frames), later waves: slower
            const spawnRate = totalSpawned < FIREWORK_COUNT * 2 ? 4 : 10;
            if (spawnTimer % spawnRate === 0) {
                const fw = new Firework(canvasEl);
                if (forcePattern) fw.pattern = forcePattern;
                fireworks.push(fw);
                totalSpawned++;
                // Occasionally spawn two at once for a volley effect
                if (Math.random() > 0.6 && totalSpawned < maxFireworks) {
                    const fw2 = new Firework(canvasEl);
                    if (forcePattern) fw2.pattern = forcePattern;
                    fireworks.push(fw2);
                    totalSpawned++;
                }
            }
        }

        // Update and draw
        for (let i = fireworks.length - 1; i >= 0; i--) {
            fireworks[i].update();
            fireworks[i].draw(ctx);
            if (fireworks[i].done) {
                fireworks.splice(i, 1);
            }
        }

        // Stop when all fireworks are done and we've spawned them all
        if (fireworks.length === 0 && totalSpawned >= maxFireworks) {
            // Final clear
            ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
            cleanup();
            return;
        }

        animationId = requestAnimationFrame(frame);
    }

    function cleanup() {
        if (animationId) {
            cancelAnimationFrame(animationId);
            animationId = null;
        }
        window.removeEventListener('resize', resize);
        if (canvasEl && canvasEl.parentNode) {
            canvasEl.remove();
        }
        canvasEl = null;
    }

    animationId = requestAnimationFrame(frame);
    return cleanup;
}

// ============================================================================
// Victory Fanfare
// ============================================================================

/**
 * Play a victory fanfare using Web Audio API.
 * Randomly picks from several short melodies.
 */
export function playVictoryFanfare() {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();

        const playNote = (freq, start, duration, gain = 0.12, type = 'square') => {
            const osc = ctx.createOscillator();
            const gainNode = ctx.createGain();

            osc.connect(gainNode);
            gainNode.connect(ctx.destination);

            osc.frequency.value = freq;
            osc.type = type;

            gainNode.gain.setValueAtTime(gain, start);
            gainNode.gain.setValueAtTime(gain * 0.8, start + duration * 0.6);
            gainNode.gain.exponentialRampToValueAtTime(0.001, start + duration);

            osc.start(start);
            osc.stop(start + duration);
        };

        const playHit = (start, duration = 0.08, gain = 0.1) => {
            const bufferSize = ctx.sampleRate * duration;
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) {
                data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / bufferSize, 3);
            }
            const source = ctx.createBufferSource();
            source.buffer = buffer;
            const gainNode = ctx.createGain();
            gainNode.gain.setValueAtTime(gain, start);
            gainNode.gain.exponentialRampToValueAtTime(0.001, start + duration);
            source.connect(gainNode);
            gainNode.connect(ctx.destination);
            source.start(start);
        };

        const now = ctx.currentTime;

        const fanfares = [
            // 1: Classic triumphant — rising C major arpeggio to big finale
            () => {
                playHit(now, 0.05, 0.08);
                playNote(523.25, now, 0.12);                          // C5
                playNote(659.25, now + 0.10, 0.12);                   // E5
                playNote(783.99, now + 0.20, 0.12);                   // G5
                playHit(now + 0.30, 0.05, 0.06);
                playNote(1046.50, now + 0.30, 0.25);                  // C6
                playNote(523.25, now + 0.30, 0.25, 0.06, 'triangle');
                playNote(392.00, now + 0.30, 0.25, 0.05, 'triangle');
                playHit(now + 0.60, 0.06, 0.08);
                playNote(1174.66, now + 0.60, 0.12);                  // D6
                playNote(1318.51, now + 0.72, 0.12);                  // E6
                playNote(1567.98, now + 0.84, 0.35);                  // G6
                playNote(783.99, now + 0.84, 0.35, 0.07, 'triangle');
                playNote(987.77, now + 0.84, 0.35, 0.06, 'triangle');
                playNote(523.25, now + 0.84, 0.35, 0.04, 'sine');
                playHit(now + 1.25, 0.08, 0.12);
                playNote(2093.00, now + 1.25, 0.5, 0.10);
                playNote(1567.98, now + 1.25, 0.5, 0.09);
                playNote(1318.51, now + 1.25, 0.5, 0.08);
                playNote(1046.50, now + 1.25, 0.5, 0.07);
                playNote(783.99, now + 1.25, 0.5, 0.05, 'triangle');
                playNote(523.25, now + 1.25, 0.5, 0.04, 'triangle');
                playNote(2637.02, now + 1.35, 0.3, 0.04, 'sine');
                playNote(3135.96, now + 1.50, 0.3, 0.03, 'sine');
                playNote(2093.00, now + 1.65, 0.4, 0.03, 'sine');
            },

            // 2: Regal brass — stately dotted rhythm, majestic feel
            () => {
                playHit(now, 0.06, 0.10);
                playNote(392.00, now, 0.20, 0.12);                    // G4
                playNote(392.00, now + 0.22, 0.08, 0.08);             // G4 short
                playNote(523.25, now + 0.32, 0.25, 0.12);             // C5
                playNote(392.00, now + 0.32, 0.25, 0.06, 'triangle');
                playHit(now + 0.60, 0.05, 0.08);
                playNote(659.25, now + 0.60, 0.20, 0.12);             // E5
                playNote(659.25, now + 0.82, 0.08, 0.08);             // E5 short
                playNote(783.99, now + 0.92, 0.30, 0.12);             // G5
                playNote(523.25, now + 0.92, 0.30, 0.06, 'triangle');
                playNote(659.25, now + 0.92, 0.30, 0.05, 'triangle');
                playHit(now + 1.28, 0.08, 0.12);
                playNote(1046.50, now + 1.28, 0.50, 0.11);            // C6
                playNote(783.99, now + 1.28, 0.50, 0.09);             // G5
                playNote(523.25, now + 1.28, 0.50, 0.07);             // C5
                playNote(392.00, now + 1.28, 0.50, 0.04, 'triangle'); // G4
                playNote(261.63, now + 1.28, 0.50, 0.04, 'sine');     // C4 bass
                playNote(1567.98, now + 1.45, 0.3, 0.04, 'sine');     // G6 sparkle
                playNote(2093.00, now + 1.58, 0.3, 0.03, 'sine');     // C7 sparkle
            },

            // 3: Bright and bouncy — playful ascending sixths into big celebration
            () => {
                // Opening: bouncy ascending sixths
                playNote(523.25, now, 0.10, 0.10);                    // C5
                playNote(880.00, now + 0.10, 0.10, 0.10);             // A5
                playNote(587.33, now + 0.22, 0.10, 0.10);             // D5
                playNote(987.77, now + 0.32, 0.10, 0.10);             // B5
                playHit(now + 0.44, 0.05, 0.08);
                playNote(659.25, now + 0.44, 0.10, 0.10);             // E5
                playNote(1046.50, now + 0.54, 0.15, 0.12);            // C6

                // Build: quick ascending run
                playNote(1174.66, now + 0.72, 0.10, 0.11);            // D6
                playNote(1318.51, now + 0.82, 0.10, 0.11);            // E6
                playHit(now + 0.94, 0.05, 0.08);
                playNote(1396.91, now + 0.94, 0.12, 0.12);            // F6
                playNote(1567.98, now + 1.08, 0.25, 0.12);            // G6
                playNote(1046.50, now + 1.08, 0.25, 0.06, 'triangle');

                // Peak: big sustained chord
                playHit(now + 1.38, 0.07, 0.12);
                playNote(2093.00, now + 1.38, 0.45, 0.11);            // C7
                playNote(1567.98, now + 1.38, 0.45, 0.09);            // G6
                playNote(1318.51, now + 1.38, 0.45, 0.08);            // E6
                playNote(1046.50, now + 1.38, 0.45, 0.06, 'triangle');// C6
                playNote(783.99, now + 1.38, 0.45, 0.05, 'triangle'); // G5
                playNote(523.25, now + 1.38, 0.45, 0.04, 'sine');     // C5

                // Sparkle cascade
                playNote(2637.02, now + 1.55, 0.25, 0.04, 'sine');    // E7
                playNote(3135.96, now + 1.65, 0.25, 0.04, 'sine');    // G7
                playNote(2093.00, now + 1.75, 0.20, 0.03, 'sine');    // C7
                playNote(2637.02, now + 1.85, 0.20, 0.03, 'sine');    // E7
                playNote(4186.01, now + 1.95, 0.35, 0.03, 'sine');    // C8 ding!
            },

            // 4: Heroic march — bold power chords with rhythmic drive
            () => {
                playHit(now, 0.06, 0.12);
                playNote(349.23, now, 0.15, 0.12);                    // F4
                playNote(523.25, now, 0.15, 0.10);                    // C5
                playHit(now + 0.18, 0.04, 0.06);
                playNote(349.23, now + 0.18, 0.10, 0.10);             // F4
                playNote(523.25, now + 0.18, 0.10, 0.08);             // C5
                playNote(698.46, now + 0.32, 0.22, 0.12);             // F5
                playNote(880.00, now + 0.32, 0.22, 0.10);             // A5
                playNote(349.23, now + 0.32, 0.22, 0.05, 'triangle');
                playHit(now + 0.58, 0.06, 0.10);
                playNote(783.99, now + 0.58, 0.15, 0.12);             // G5
                playNote(987.77, now + 0.58, 0.15, 0.10);             // B5
                playNote(1174.66, now + 0.78, 0.15, 0.12);            // D6
                playHit(now + 0.98, 0.04, 0.08);
                playNote(1046.50, now + 0.98, 0.18, 0.12);            // C6
                playNote(1318.51, now + 0.98, 0.18, 0.10);            // E6
                playHit(now + 1.20, 0.08, 0.14);
                playNote(1396.91, now + 1.20, 0.55, 0.12);            // F6
                playNote(1046.50, now + 1.20, 0.55, 0.09);            // C6
                playNote(880.00, now + 1.20, 0.55, 0.07);             // A5
                playNote(698.46, now + 1.20, 0.55, 0.05, 'triangle');
                playNote(349.23, now + 1.20, 0.55, 0.04, 'sine');     // F4 bass
                playNote(2093.00, now + 1.40, 0.3, 0.04, 'sine');
                playNote(2793.83, now + 1.55, 0.3, 0.03, 'sine');     // F7
            },
        ];

        const index = arguments[0] != null ? arguments[0] : Math.floor(Math.random() * fanfares.length);
        fanfares[Math.min(index, fanfares.length - 1)]();

        setTimeout(() => ctx.close().catch(() => {}), 3000);
    } catch (e) {
        // Audio not available, silent fail
    }
}

const FANFARE_NAMES = ['Classic Triumphant', 'Regal Brass', 'Bright & Bouncy', 'Heroic March'];

// ============================================================================
// Pixel Blimp with LED text scroller
// ============================================================================

class PixelBlimp {
    constructor(canvas, messages) {
        this.canvas = canvas;
        this.px = 5;

        // All messages joined into one continuous ticker
        this.text = messages.join('   \u2605   ');

        // Envelope shape: each row is [startCol, endCol]
        this.body = [
            [9, 21], [6, 24], [4, 26], [3, 28],
            [2, 29], [2, 29], [2, 29], [2, 29], [2, 29],
            [3, 28], [4, 26], [6, 24], [9, 21],
        ];

        // Tail fins at the back (right side) — squared-off stabilizers
        // Negative rows extend above the body envelope
        this.fins = [
            // Upper fin (extends above body)
            [-1, 25, 30], [0, 24, 31], [1, 24, 31],
            [2, 24, 31], [3, 24, 31], [4, 25, 30],
            // Lower fin (extends below body)
            [8, 25, 30], [9, 24, 31], [10, 24, 31],
            [11, 24, 31], [12, 24, 31], [13, 25, 30],
        ];

        // Banner region (rows 5-7, inset from edges)
        this.bannerRow0 = 5;
        this.bannerRow1 = 7;
        this.bannerCol0 = 4;
        this.bannerCol1 = 28;

        this.pxWidth = 32 * this.px;

        // Position & motion
        this.x = canvas.width + 20;
        this.y = canvas.height * (0.12 + Math.random() * 0.15);
        this.speed = 0.4 + Math.random() * 0.1;
        this.bobPhase = Math.random() * Math.PI * 2;

        // Text scroll
        this.scrollX = 0;
        this.textWidth = null;
    }

    update() {
        this.x -= this.speed;
        this.bobPhase += 0.012;
        this.scrollX += 0.35;

        if (this.x < -(this.pxWidth + 40)) {
            this.x = this.canvas.width + 20;
            this.y = this.canvas.height * (0.12 + Math.random() * 0.15);
        }
    }

    draw(ctx) {
        const px = this.px;
        const bx = Math.round(this.x);
        const by = Math.round(this.y + Math.sin(this.bobPhase) * 6);

        ctx.globalAlpha = 0.85;

        // Tail fins (drawn first, behind body)
        ctx.fillStyle = '#222e5c';
        for (const [row, c0, c1] of this.fins) {
            ctx.fillRect(bx + c0 * px, by + row * px, (c1 - c0) * px, px);
        }

        // Body gradient: highlight on top, shadow on bottom
        const bodyColors = [
            '#4a5a9a', '#4a5a9a',
            '#3a4a88', '#3a4a88',
            '#2a3a70',
            '#1e2a5a', '#1e2a5a', '#1e2a5a',
            '#2a3a70',
            '#22306a', '#22306a',
            '#1a2854', '#1a2854',
        ];

        for (let r = 0; r < this.body.length; r++) {
            const [c0, c1] = this.body[r];
            ctx.fillStyle = bodyColors[r];
            ctx.fillRect(bx + c0 * px, by + r * px, (c1 - c0) * px, px);
        }

        // Banner background (dark strip for LED text)
        const bnX = bx + this.bannerCol0 * px;
        const bnY = by + this.bannerRow0 * px;
        const bnW = (this.bannerCol1 - this.bannerCol0) * px;
        const bnH = (this.bannerRow1 - this.bannerRow0 + 1) * px;
        ctx.fillStyle = '#0a0a18';
        ctx.fillRect(bnX, bnY, bnW, bnH);

        // Banner edge lines
        ctx.fillStyle = '#333366';
        ctx.fillRect(bnX, bnY, bnW, 1);
        ctx.fillRect(bnX, bnY + bnH - 1, bnW, 1);

        // Scrolling text clipped to banner
        ctx.save();
        ctx.beginPath();
        ctx.rect(bnX + 1, bnY + 1, bnW - 2, bnH - 2);
        ctx.clip();

        const fontSize = Math.max(7, bnH - 3);
        ctx.font = `bold ${fontSize}px monospace`;
        ctx.fillStyle = '#f0c040';
        ctx.globalAlpha = 0.95;

        if (this.textWidth === null) {
            this.textWidth = ctx.measureText(this.text).width;
        }

        const totalScroll = bnW + this.textWidth;
        const offset = this.scrollX % totalScroll;
        ctx.fillText(this.text, bnX + bnW - offset, bnY + bnH - 2);

        ctx.restore();

        // Gondola cables
        ctx.globalAlpha = 0.5;
        ctx.strokeStyle = '#666688';
        ctx.lineWidth = 1;
        const gondolaY = by + this.body.length * px + 2 * px;
        const gondolaX = bx + 11 * px;
        const gondolaW = 10 * px;
        ctx.beginPath();
        ctx.moveTo(gondolaX + 2, gondolaY);
        ctx.lineTo(bx + 12 * px, by + (this.body.length - 1) * px);
        ctx.moveTo(gondolaX + gondolaW - 2, gondolaY);
        ctx.lineTo(bx + 20 * px, by + (this.body.length - 1) * px);
        ctx.stroke();

        // Gondola body
        ctx.globalAlpha = 0.85;
        ctx.fillStyle = '#3a3a5a';
        ctx.fillRect(gondolaX, gondolaY, gondolaW, 2 * px);
        ctx.fillStyle = '#2a2a4a';
        ctx.fillRect(gondolaX, gondolaY + 2 * px, gondolaW, px);

        // Blinking nav light on upper fin tip
        if (Math.sin(Date.now() * 0.003) > 0.3) {
            ctx.globalAlpha = 0.9;
            ctx.fillStyle = '#ff4444';
            ctx.fillRect(bx + 30 * px, by + -1 * px, px, px);
        }

        ctx.globalAlpha = 1;
    }
}

// ============================================================================
// Ambient Fireworks (for soft game over)
// ============================================================================

/**
 * Start subtle ambient fireworks inside a container element.
 * Uses transparent background so fireworks appear over the overlay's dark backdrop.
 * Spawns slowly (~1 firework every 90 frames) and runs indefinitely.
 * Optionally shows a pixel blimp with scrolling stat messages.
 * @param {HTMLElement} container - Element to insert the canvas into
 * @param {string[]} [blimpMessages] - Messages to scroll on the blimp banner
 * @returns {function} cleanup function to stop the animation
 */
export function startAmbientFireworks(container, blimpMessages) {
    const canvas = document.createElement('canvas');
    canvas.className = 'ambient-fireworks-canvas';
    // Insert as first child so the modal (appended after) sits on top via DOM order
    container.insertBefore(canvas, container.firstChild);

    const ctx = canvas.getContext('2d');
    let ambientAnimId = null;

    const resize = () => {
        canvas.width = container.clientWidth;
        canvas.height = container.clientHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const fireworks = [];
    let spawnTimer = 0;

    // Create blimp if messages provided
    const blimp = (blimpMessages && blimpMessages.length > 0)
        ? new PixelBlimp(canvas, blimpMessages)
        : null;

    function frame() {
        // Clear each frame (transparent background — no trail accumulation)
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw blimp first (behind fireworks)
        if (blimp) {
            blimp.update();
            blimp.draw(ctx);
        }

        // Spawn ~1 firework every 90 frames (~1.5s at 60fps)
        spawnTimer++;
        if (spawnTimer % 90 === 0) {
            fireworks.push(new Firework(canvas));
        }

        // Update and draw
        for (let i = fireworks.length - 1; i >= 0; i--) {
            fireworks[i].update();
            fireworks[i].draw(ctx);
            if (fireworks[i].done) {
                fireworks.splice(i, 1);
            }
        }

        ambientAnimId = requestAnimationFrame(frame);
    }

    function cleanup() {
        if (ambientAnimId) {
            cancelAnimationFrame(ambientAnimId);
            ambientAnimId = null;
        }
        window.removeEventListener('resize', resize);
        if (canvas && canvas.parentNode) {
            canvas.remove();
        }
    }

    ambientAnimId = requestAnimationFrame(frame);
    return cleanup;
}

// ============================================================================
// Combined celebration
// ============================================================================

/**
 * Trigger the full celebration: fireworks + fanfare
 * @returns {function} cleanup function
 */
export function celebrate() {
    playVictoryFanfare();
    return startFireworks();
}

// ============================================================================
// Fake Scoreboard (for test panel)
// ============================================================================

function showFakeScoreboard() {
    // Remove existing
    const existing = document.getElementById('game-scoreboard');
    if (existing) existing.remove();

    const fakePlayers = [
        { nickname: 'Alice', score: 342, wordsPlayed: 12 },
        { nickname: 'Bob', score: 287, wordsPlayed: 10 },
        { nickname: 'Charlie', score: 195, wordsPlayed: 8 },
        { nickname: 'Frank', score: 0, wordsPlayed: 0 },
    ];
    const medals = ['\uD83E\uDD47', '\uD83E\uDD48', '\uD83E\uDD49'];

    const overlay = document.createElement('div');
    overlay.id = 'game-scoreboard';
    overlay.className = 'scoreboard-overlay';

    const modal = document.createElement('div');
    modal.className = 'scoreboard-modal';

    const title = document.createElement('h2');
    title.className = 'scoreboard-title';
    title.textContent = 'Game Over!';
    modal.appendChild(title);

    const winnerEl = document.createElement('div');
    winnerEl.className = 'scoreboard-winner';
    winnerEl.textContent = '\uD83C\uDFC6 Alice wins!';
    modal.appendChild(winnerEl);

    const list = document.createElement('ul');
    list.className = 'scoreboard-list';
    fakePlayers.forEach((player, i) => {
        const li = document.createElement('li');
        li.className = 'scoreboard-player';
        const medal = document.createElement('span');
        medal.className = 'scoreboard-medal';
        medal.textContent = medals[i];
        li.appendChild(medal);
        const name = document.createElement('span');
        name.className = 'scoreboard-name';
        name.textContent = player.nickname;
        li.appendChild(name);
        const score = document.createElement('span');
        score.className = 'scoreboard-score';
        score.textContent = player.score === 0 ? '\uD83E\uDD5A' : player.score;
        li.appendChild(score);
        const avg = document.createElement('span');
        avg.className = 'scoreboard-avg';
        const avgScore = player.wordsPlayed > 0 ? (player.score / player.wordsPlayed).toFixed(1) : '0';
        avg.textContent = `(${avgScore}/play)`;
        li.appendChild(avg);
        list.appendChild(li);
    });
    modal.appendChild(list);

    // Stats row
    const statsRow = document.createElement('div');
    statsRow.className = 'scoreboard-stats';
    [['42', 'Words'], ['+98', 'Best Play'], ['30', 'Turns']].forEach(([val, label]) => {
        const stat = document.createElement('div');
        stat.className = 'scoreboard-stat';
        const v = document.createElement('span');
        v.className = 'scoreboard-stat-value';
        v.textContent = val;
        const l = document.createElement('span');
        l.className = 'scoreboard-stat-label';
        l.textContent = label;
        stat.appendChild(v);
        stat.appendChild(l);
        statsRow.appendChild(stat);
    });
    modal.appendChild(statsRow);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.className = 'scoreboard-btn';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => {
        cleanup();
    });
    modal.appendChild(closeBtn);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    // Ambient fireworks + blimp
    const blimpMessages = [
        'Best play: QUARTZ +98 by Bob',
    ];
    const cleanupAmbient = startAmbientFireworks(overlay, blimpMessages);
    const cleanupCelebration = celebrate();

    function cleanup() {
        overlay.remove();
        if (cleanupAmbient) cleanupAmbient();
        if (cleanupCelebration) cleanupCelebration();
    }

    return cleanup;
}

// ============================================================================
// Test Panel — accessible via ?fireworks in URL
// ============================================================================

/**
 * Show a floating test panel for previewing firework patterns and fanfares.
 */
export function showFireworksTestPanel() {
    // Remove existing panel if any
    const existing = document.getElementById('fireworks-test-panel');
    if (existing) { existing.remove(); return; }

    let activeCleanup = null;

    const panel = document.createElement('div');
    panel.id = 'fireworks-test-panel';
    panel.style.cssText = `
        position: fixed; bottom: 20px; right: 20px; z-index: 9999;
        background: #1a1a2e; border: 2px solid #f0c040; border-radius: 12px;
        padding: 16px; font-family: monospace; color: #eee; min-width: 220px;
        box-shadow: 0 4px 24px rgba(0,0,0,0.5);
    `;

    const title = document.createElement('div');
    title.textContent = 'Fireworks Test';
    title.style.cssText = 'font-size: 14px; font-weight: bold; color: #f0c040; margin-bottom: 12px; text-align: center;';
    panel.appendChild(title);

    const makeBtn = (label, onClick) => {
        const btn = document.createElement('button');
        btn.textContent = label;
        btn.style.cssText = `
            display: block; width: 100%; padding: 8px 12px; margin: 4px 0;
            background: #2a2a4a; color: #eee; border: 1px solid #444;
            border-radius: 6px; cursor: pointer; font-family: monospace; font-size: 12px;
            text-align: left;
        `;
        btn.onmouseenter = () => btn.style.background = '#3a3a5a';
        btn.onmouseleave = () => btn.style.background = '#2a2a4a';
        btn.onclick = () => {
            if (activeCleanup) { activeCleanup(); activeCleanup = null; }
            onClick();
        };
        return btn;
    };

    // Section: Firework patterns
    const fwLabel = document.createElement('div');
    fwLabel.textContent = 'Patterns';
    fwLabel.style.cssText = 'font-size: 11px; color: #888; margin: 10px 0 4px; text-transform: uppercase; letter-spacing: 1px;';
    panel.appendChild(fwLabel);

    for (const pattern of BURST_PATTERNS) {
        panel.appendChild(makeBtn(pattern, () => {
            activeCleanup = startFireworks(pattern);
        }));
    }
    panel.appendChild(makeBtn('random (all)', () => {
        activeCleanup = startFireworks();
    }));

    // Section: Fanfares
    const sfLabel = document.createElement('div');
    sfLabel.textContent = 'Fanfares';
    sfLabel.style.cssText = 'font-size: 11px; color: #888; margin: 10px 0 4px; text-transform: uppercase; letter-spacing: 1px;';
    panel.appendChild(sfLabel);

    FANFARE_NAMES.forEach((name, i) => {
        panel.appendChild(makeBtn(name, () => {
            playVictoryFanfare(i);
        }));
    });

    // Section: Combined
    const cLabel = document.createElement('div');
    cLabel.textContent = 'Combined';
    cLabel.style.cssText = 'font-size: 11px; color: #888; margin: 10px 0 4px; text-transform: uppercase; letter-spacing: 1px;';
    panel.appendChild(cLabel);

    panel.appendChild(makeBtn('Full Celebration (random)', () => {
        activeCleanup = celebrate();
    }));

    // Section: Scoreboard
    const sbLabel = document.createElement('div');
    sbLabel.textContent = 'Scoreboard';
    sbLabel.style.cssText = 'font-size: 11px; color: #888; margin: 10px 0 4px; text-transform: uppercase; letter-spacing: 1px;';
    panel.appendChild(sbLabel);

    panel.appendChild(makeBtn('Fake Scoreboard + Blimp', () => {
        activeCleanup = showFakeScoreboard();
    }));

    // Close button
    const close = document.createElement('div');
    close.textContent = '\u00d7';
    close.style.cssText = `
        position: absolute; top: 8px; right: 12px; cursor: pointer;
        color: #888; font-size: 18px; line-height: 1;
    `;
    close.onclick = () => {
        if (activeCleanup) activeCleanup();
        panel.remove();
    };
    panel.appendChild(close);

    document.body.appendChild(panel);
}

// Auto-show test panel if ?fireworks is in the URL
if (typeof window !== 'undefined' && window.location.search.includes('fireworks')) {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', showFireworksTestPanel);
    } else {
        showFireworksTestPanel();
    }
}
